import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import axios from "../../api/axios";
import logoADUTEM from "../../assets/img/logoadutem.png";

// ───────────────────────────────────────────────
// Componente principal - DashboardSocio
// ───────────────────────────────────────────────
const DashboardSocio = () => {
  const navigate = useNavigate();

  const [ultimoMes, setUltimoMes] = useState(null);
  const [usuario, setUsuario] = useState(null);
  const [proximaReserva, setProximaReserva] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [perfilRes, resumenRes, reservasRes] = await Promise.all([
          axios.get("/perfil"),
          axios.get("/mi-total-mensual"),
          axios.get("/mis-reservas"),
        ]);

        if (perfilRes.data?.ok) {
          setUsuario(perfilRes.data.perfil || perfilRes.data.usuario || null);
        }

        setUltimoMes(resumenRes.data.meses?.[0] ?? null);

        if (reservasRes.data?.ok && reservasRes.data.proximaReserva) {
          setProximaReserva(reservasRes.data.proximaReserva);
        }
      } catch (err) {
        console.error("Error al cargar datos del dashboard:", err);
        const mensaje = err.response?.data?.msg || err.response?.data?.message || err.message || "No se pudo cargar la información";
        setError(mensaje);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={() => navigate(0)} />;

  const socioNombre = usuario?.nombres || "Socio";
  const socioApellido = usuario?.apellidos || "";
  const nombreCompleto = `${socioNombre} ${socioApellido}`.trim();
  const primeraLetra = socioNombre.charAt(0).toUpperCase() || "D";

  return (
    <>
      <Navbar socioNombre={nombreCompleto} primeraLetra={primeraLetra} />

      <main className="container py-5">
        <header className="text-center mb-5">
          <h1 className="fw-bold display-5" style={{ color: "#1f2a37" }}>
            Bienvenido 👋, {socioNombre.split(" ")[0]}
          </h1>
          <p className="text-muted fs-5 mt-2">Aquí tienes el resumen de tu cuenta</p>
        </header>

        <div className="row g-4 justify-content-center">
          {/* Total Aportado */}
          <div className="col-12 col-sm-6 col-lg-3">
            <StatCard
              title="Total Aportado"
              value={`$${ultimoMes?.total ?? 0}`}
              icon="bi-currency-dollar"
              color="#818683"
              subtitle="Este mes"
            />
          </div>

          {/* Cuotas Pagadas */}
          <div className="col-12 col-sm-6 col-lg-3">
            <StatCard
  title="Cuotas Pagadas"
  value={`$${ultimoMes?.total_cuotas ?? 0}`}
  icon="bi-check-lg"
  color="#28a745" // Verde
  subtitle="Este mes"
/>
          </div>

          {/* Descuentos Aplicados */}
          <div className="col-12 col-sm-6 col-lg-3">
            <StatCard
  title="Descuentos Aplicados"
  value={`$${ultimoMes?.total_descuentos ?? 0}`}
  icon="bi-exclamation-lg"
  color="#ffc107" // Amarillo
  subtitle="Este mes"
/>
          </div>

          {/* Próxima Reserva */}
          <div className="col-12 col-sm-6 col-lg-3">
            <StatCard
              title="Próxima Reserva"
              value={
                proximaReserva ? (
                  <div className="text-start fs-6 lh-sm">
                    <strong>{proximaReserva.espacio}</strong><br />
                    {proximaReserva.fecha} • {proximaReserva.hora_inicio || "?"} – {proximaReserva.hora_fin || "?"}
                    <br />
                    <span className="badge bg-light text-dark mt-2">{proximaReserva.estado}</span>
                  </div>
                ) : (
                  <div>
                    <span className="fw-medium" style={{ color: "#0d6efd" }}>Sin reserva próxima</span>
                    <Link
  to="/reservasSocios"
  className="d-block text-muted mt- text-decoration-none"
>
  Haz tu reserva ahora
</Link>
                  </div>
                )
              }
              icon="bi-calendar-event"
              color="#fd910d"
            />
          </div>
        </div>
      </main>

      <footer className="text-center mt-5 pb-4">
  <div className="d-flex justify-content-center gap-4">

   <div className="d-flex justify-content-center gap-4">
  <a
    href="https://www.instagram.com/adutem2025?igsh=MWU2aXJhZnh1Zjhtag=="
    target="_blank"
    rel="noopener noreferrer"
    style={{ color: "#E4405F" }}
    className="fs-2"
  >
    <i className="bi bi-instagram"></i>
  </a>

  <a
    href="https://www.facebook.com/share/1EQhuaFzqH/?mibextid=wwXIfr"
    target="_blank"
    rel="noopener noreferrer"
    style={{ color: "#1877F2" }}
    className="fs-2"
  >
    <i className="bi bi-facebook"></i>
  </a>

  <a
    href="https://youtube.com/@adutem?si=dluaO2vDTwiUwyfh"
    target="_blank"
    rel="noopener noreferrer"
    style={{ color: "#FF0000" }}
    className="fs-2"
  >
    <i className="bi bi-youtube"></i>
  </a>
</div>

  </div>

  <p className="text-muted small mt-3 mb-0">
    © {new Date().getFullYear()} ADUTEM • Síguenos en redes sociales
  </p>
</footer>
    </>
  );
};

// ───────────────────────────────────────────────
// NAVBAR MEJORADO - Logo más grande (68px) sin texto ADUTEM
// ───────────────────────────────────────────────
const Navbar = ({ socioNombre, primeraLetra }) => {
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login", { replace: true });
  };

  return (
    <nav 
      className="navbar navbar-expand-lg navbar-dark shadow-sm"
      style={{
  background: "#FFD123",
  padding: "18px 0",
  position: "sticky",
  top: 0,
  zIndex: 1000,
}}
    >
      <div className="container-fluid px-4">
        
        {/* Logo más grande */}
        <Link className="navbar-brand d-flex align-items-center" to="/DashboardSocio">
          <img 
            src={logoADUTEM} 
            alt="ADUTEM" 
            style={{ height: "68px" }} 
          />
        </Link>

        {/* Botón hamburguesa */}
        <button
          className="navbar-toggler border-0"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarNav"
          aria-controls="navbarNav"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        {/* Menú de navegación */}
        <div className="collapse navbar-collapse" id="navbarNav">
          <ul className="navbar-nav mx-auto gap-1">
            {[
              { to: "/DashboardSocio", label: "Inicio", end: true },
              { to: "/vercuotas", label: "Mis Cuotas" },
              { to: "/reservasSocios", label: "Reservas" },
              { to: "/MisReservas", label: "Mis Reservas" },
            ].map(({ to, label, end = false }) => (
              <li className="nav-item" key={to}>
                <NavLink
                  to={to}
                  end={end}
                  className={({ isActive }) => 
                    `nav-link px-4 py-2 rounded-pill fw-medium transition-all ${isActive 
                      ? "active bg-white text-dark shadow-sm" 
                      : "text-white hover-bg-white hover-text-dark"}`
                  }
                  style={{ fontSize: "1.02rem" }}
                >
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>

          {/* Perfil / Avatar */}
          <div className="dropdown">
            <button
              className="btn btn-link text-decoration-none p-0 border-0 d-flex align-items-center gap-2"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <div
                className="rounded-circle d-flex align-items-center justify-content-center shadow"
                style={{
                  width: "42px",
                  height: "42px",
                  backgroundColor: "#f1c40f",
                  color: "#2c3e50",
                  fontWeight: "700",
                  fontSize: "1.25rem",
                  border: "2px solid rgba(255,255,255,0.6)"
                }}
              >
                {primeraLetra}
              </div>
              <div className="text-white text-start d-none d-md-block">
                <div className="fw-medium" style={{ fontSize: "1.02rem" }}>{socioNombre}</div>
                <small className="opacity-75" style={{ fontSize: "0.82rem" }}>Socio</small>
              </div>
              <i className="bi bi-chevron-down text-white ms-1" style={{ fontSize: "1rem" }}></i>
            </button>

            <ul className="dropdown-menu dropdown-menu-start shadow-lg border-0 py-2">
              <li>
                <Link className="dropdown-item d-flex align-items-center py-2" to="/perfil">
                  <i className="bi bi-person-circle me-3 fs-5"></i>
                  Mi Perfil
                </Link>
              </li>
              <li><hr className="dropdown-divider my-1" /></li>
              <li>
                <button 
                  className="dropdown-item d-flex align-items-center py-2 text-danger" 
                  onClick={handleLogout}
                >
                  <i className="bi bi-box-arrow-right me-3 fs-5"></i>
                  Cerrar sesión
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </nav>
  );
};

// ───────────────────────────────────────────────
// StatCard (sin cambios - estilo que te gustó)
// ───────────────────────────────────────────────
const StatCard = ({ title, value, icon, color, subtitle }) => (
  <div className="card border-0 shadow h-100" style={{ borderRadius: "16px", overflow: "hidden" }}>
    
    <div 
      className="d-flex justify-content-center pt-4 pb-3"
      style={{ 
        backgroundColor: color + "15",
        borderBottom: `4px solid ${color}`
      }}
    >
      <div 
        className="rounded-circle d-flex align-items-center justify-content-center"
        style={{
          width: "78px",
          height: "78px",
          backgroundColor: color,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
        }}
      >
        <i className={`bi ${icon} fs-1`} style={{ color: "white", fontSize: "42px" }} />
      </div>
    </div>

    <div className="card-body text-center py-4">
      <h5 className="text-muted fw-medium mb-3">{title}</h5>
      
      <div className="fs-3 fw-bold mb-1" style={{ color: color }}>
        {value}
      </div>

      {subtitle && (
        <small className="text-muted d-block">{subtitle}</small>
      )}
    </div>
  </div>
);

// Loading y Error States
const LoadingState = () => (
  <div className="min-vh-100 d-flex align-items-center justify-content-center">
    <div className="text-center">
      <div className="spinner-border text-success" style={{ width: "3.5rem", height: "3.5rem" }} />
      <p className="mt-4 text-muted fs-5">Cargando tu panel...</p>
    </div>
  </div>
);

const ErrorState = ({ message, onRetry }) => (
  <div className="min-vh-100 d-flex align-items-center justify-content-center p-3">
    <div className="alert alert-danger text-center shadow p-4" style={{ maxWidth: "500px" }}>
      <i className="bi bi-exclamation-triangle-fill me-3 fs-3"></i>
      <h4>Algo salió mal</h4>
      <p>{message}</p>
      <button className="btn btn-outline-danger" onClick={onRetry}>Intentar de nuevo</button>
    </div>
  </div>
);

export default DashboardSocio;